/**
 * XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX
 *
 * THIS FILE IS MANAGED CENTRALLY BY THE `common-code` REPO.
 * IT COULD BE AUTO-REPLACED AT ANY TIME.
 * DO NOT MAKE CUSTOM CHANGES TO THIS FILE.
 * @see https://gitlab.com/dea-aero/development/common-code
 *
 * XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX
 */
import useWebSocket, {ReadyState} from 'react-use-websocket';
import {useCallback, useEffect, useMemo, useRef} from "react";
import {useLocation} from "react-router-dom";

import {createStore as create} from "../util/createStore";
import {getServer} from "../util/server";
import {useServerStore} from "../util/useServerStore";
import {useLoginStore} from "../useLoginStore";
import {useInterval} from "../util/useInterval";
import {useApiStore} from "../util/Api";


export const useWebsocketStore = create((set) => ({
    send: null,
    setSend: (send) => set({ send }),

    _msgHandlers: [],
    msgHandler: msg => set(state => {
        state._msgHandlers.forEach(mh => mh(msg));
        return {};
    }),
    addMsgHandler: (cb) => set(state => ({ _msgHandlers: [...state._msgHandlers, cb]})),
    removeMsgHandler: (cb) => set(state => ({ _msgHandlers: state._msgHandlers.filter(c => c !== cb)})),
}))


export default function WebSock() {
    console.log('redraw', 'WebSock');
    // Discard or dont provide the WebSockConn object if user not logged in
    const user = useLoginStore(state => state.user);
    return user ? <WebSockConn/> : <></>
}


const WebSockConn = () => {
    console.log('redraw', 'WebSock/WebSockConn');
    const setServertime = useServerStore(state => state.setServertime);
    const servertimediff = useServerStore(state => state.servertimediff);
    const setServertimediff = useServerStore(state => state.setServertimediff);
    const websockMsgHandler = useWebsocketStore(state => state.msgHandler);
    const setSend = useWebsocketStore(state => state.setSend);
    const send = useWebsocketStore(state => state.send);
    const asUser = useApiStore(state => state.asUser);
    const addMsgHandler = useWebsocketStore(state => state.addMsgHandler);
    const removeMsgHandler = useWebsocketStore(state => state.removeMsgHandler);

    const didUnmount = useRef(false);
    const srv = getServer().replace('https://', '')
    const location = useLocation();

    useEffect(() => {
        if (send)
            return send(JSON.stringify({at_path: location.pathname}))
    }, [location.pathname, send])

    const socketUrl = useMemo(() => {
        if (asUser)
            return `wss://${srv}/ws?x-as-user=${asUser}`
        return `wss://${srv}/ws`
    }, [srv, asUser])

    const {sendMessage, readyState, getWebSocket} = useWebSocket(socketUrl, {
        shouldReconnect: () => didUnmount.current === false,
        reconnectInterval: 1000,
        reconnectAttempts: 999999,
        retryOnError: 1,
        onMessage: websockMsgHandler,
        disableJson: true,  // prevent the internal Websock handler from doing its own json parsing
        filter: () => false,  // prevents any message being returned through latestMsg property, onMessage works
    });

    useEffect(() => {
        didUnmount.current = false
        // prevent unmounted websockets from reconnecting
        return () => {didUnmount.current = true}
    }, []);

    useEffect(() => {
        const ws = getWebSocket()
        if (ws) ws.binaryType = 'arraybuffer'
    }, [getWebSocket, readyState]);

    useEffect(() => {
      const connectionStatus = {
        [ReadyState.CONNECTING]: 'Connecting',
        [ReadyState.OPEN]: 'Open',
        [ReadyState.CLOSING]: 'Closing',
        [ReadyState.CLOSED]: 'Closed',
        [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
      }[readyState]
        console.log('ws connection state changed', connectionStatus)
        if (connectionStatus === 'Open') {
            setSend(sendMessage)
        } else {
            setSend(null)
        }
    }, [readyState, setSend, sendMessage])

    const setServerTimeDiff = useCallback(() => {
        if (typeof servertimediff === 'number') {
            const now = ((Date.now() / 1000) + servertimediff)
            setServertime(now)
        }
    }, [servertimediff, setServertime]);
    useInterval(setServerTimeDiff, 200);

    /**
     * Process messages received via websocket
     */
    const processMsg = useCallback(msg => {
        if (msg === null) return

        if (typeof msg.data !== 'string') {
            return
        }
        try {
            const data = JSON.parse(msg.data)
            if (typeof data.servertime === 'number') {
                const local = Date.now() / 1000;
                const diff = local - data.servertime
                console.log('servertime', {server: data.servertime, local, diff})
                setServertimediff(diff)
            }
        } catch (e) {
            console.error('failed parsing message object', e, msg.data)
        }
    }, [setServertimediff]);

    useEffect(() => {
        addMsgHandler(processMsg);
        return () => removeMsgHandler(processMsg);
    }, [processMsg, addMsgHandler, removeMsgHandler]);
}
